import { GovernmentDeduction, GovernmentDeductionSummary } from '../services/governmentDeductionsService';
import { formatDate } from './dateUtils';
import { formatCurrency } from './currencyutils';

/**
 * Convert government deductions data to CSV format
 */
function convertDeductionsToCSV(deductions: GovernmentDeduction[], headers: string[]): string {
  const csvRows = [headers.join(',')];
  
  deductions.forEach(deduction => {
    const row = [
      deduction.employee_number || '',
      `"${deduction.employee_name}"`,
      deduction.sss_number || '',
      deduction.philhealth_number || '',
      deduction.pagibig_number || '',
      deduction.tin_number || '',
      deduction.salary?.toString() || '0',
      deduction.sss_contribution?.toString() || '0',
      deduction.philhealth_contribution?.toString() || '0',
      deduction.pagibig_contribution?.toString() || '0',
      deduction.withholding_tax?.toString() || '0',
      deduction.total_deductions?.toString() || '0'
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Convert SSS deductions to CSV format
 */
function convertSSSToCSV(deductions: GovernmentDeduction[]): string {
  const headers = [
    'Employee Number',
    'Employee Name',
    'SSS Number',
    'Monthly Salary',
    'SSS Contribution',
    'Status'
  ];
  
  const csvRows = [headers.join(',')];
  
  deductions.forEach(deduction => {
    const row = [
      deduction.employee_number || '',
      `"${deduction.employee_name}"`,
      deduction.sss_number || '',
      deduction.salary?.toString() || '0',
      deduction.sss_contribution?.toString() || '0',
      deduction.deduct_sss ? 'Active' : 'Inactive'
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Convert PhilHealth deductions to CSV format
 */
function convertPhilHealthToCSV(deductions: GovernmentDeduction[]): string {
  const headers = [
    'Employee Number',
    'Employee Name',
    'PhilHealth Number',
    'Monthly Salary',
    'PhilHealth Contribution',
    'Status'
  ];
  
  const csvRows = [headers.join(',')];
  
  deductions.forEach(deduction => {
    const row = [
      deduction.employee_number || '',
      `"${deduction.employee_name}"`,
      deduction.philhealth_number || '',
      deduction.salary?.toString() || '0',
      deduction.philhealth_contribution?.toString() || '0',
      deduction.deduct_philhealth ? 'Active' : 'Inactive'
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Convert Pag-IBIG deductions to CSV format
 */
function convertPagIBIGToCSV(deductions: GovernmentDeduction[]): string {
  const headers = [
    'Employee Number',
    'Employee Name',
    'Pag-IBIG Number',
    'Monthly Salary',
    'Pag-IBIG Contribution',
    'Status'
  ];
  
  const csvRows = [headers.join(',')];
  
  deductions.forEach(deduction => {
    const row = [
      deduction.employee_number || '',
      `"${deduction.employee_name}"`,
      deduction.pagibig_number || '',
      deduction.salary?.toString() || '0',
      deduction.pagibig_contribution?.toString() || '0',
      deduction.deduct_pagibig ? 'Active' : 'Inactive'
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Convert Tax deductions to CSV format
 */
function convertTaxToCSV(deductions: GovernmentDeduction[]): string {
  const headers = [
    'Employee Number',
    'Employee Name',
    'TIN Number',
    'Monthly Salary',
    'Withholding Tax',
    'Status'
  ];
  
  const csvRows = [headers.join(',')];
  
  deductions.forEach(deduction => {
    const row = [
      deduction.employee_number || '',
      `"${deduction.employee_name}"`,
      deduction.tin_number || '',
      deduction.salary?.toString() || '0',
      deduction.withholding_tax?.toString() || '0',
      deduction.deduct_tax ? 'Active' : 'Inactive'
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Convert all government deductions to comprehensive CSV format
 */
function convertAllDeductionsToCSV(deductions: GovernmentDeduction[]): string {
  const headers = [
    'Employee Number',
    'Employee Name',
    'SSS Number',
    'PhilHealth Number',
    'Pag-IBIG Number',
    'TIN Number',
    'Monthly Salary',
    'SSS Contribution',
    'PhilHealth Contribution',
    'Pag-IBIG Contribution',
    'Withholding Tax',
    'Total Deductions'
  ];
  
  return convertDeductionsToCSV(deductions, headers);
}

/**
 * Convert summary report to CSV format
 */
function convertSummaryToCSV(summary: GovernmentDeductionSummary): string {
  const csvRows = [];
  
  // Add summary header
  csvRows.push('Government Deductions Summary Report');
  csvRows.push(`Period: ${summary.period}`);
  csvRows.push(`Total Employees: ${summary.total_employees}`);
  csvRows.push('');
  
  // Add totals
  csvRows.push('Deduction Type,Total Amount');
  csvRows.push(`SSS Contributions,${summary.total_sss}`);
  csvRows.push(`PhilHealth Contributions,${summary.total_philhealth}`);
  csvRows.push(`Pag-IBIG Contributions,${summary.total_pagibig}`);
  csvRows.push(`Withholding Tax,${summary.total_tax}`);
  csvRows.push(`Total All Deductions,${summary.total_all_deductions}`);
  csvRows.push('');
  
  // Add detailed breakdown
  csvRows.push('Employee Details:');
  const detailHeaders = [
    'Employee Number',
    'Employee Name',
    'SSS',
    'PhilHealth',
    'Pag-IBIG',
    'Tax',
    'Total'
  ];
  csvRows.push(detailHeaders.join(','));
  
  summary.deductions.forEach(deduction => {
    const row = [
      deduction.employee_number || '',
      `"${deduction.employee_name}"`,
      deduction.sss_contribution?.toString() || '0',
      deduction.philhealth_contribution?.toString() || '0',
      deduction.pagibig_contribution?.toString() || '0',
      deduction.withholding_tax?.toString() || '0',
      deduction.total_deductions?.toString() || '0'
    ];
    csvRows.push(row.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Generate filename with current date
 */
function generateFilename(type: string, period?: string): string {
  const currentDate = new Date();
  const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD
  const periodStr = period || currentDate.toISOString().slice(0, 7); // YYYY-MM
  
  return `${type}_deductions_${periodStr}_${dateStr}.csv`;
}

// Export functions
export const governmentDeductionsExportUtils = {
  /**
   * Export SSS deductions to CSV
   */
  exportSSS: (deductions: GovernmentDeduction[], period?: string) => {
    const csvContent = convertSSSToCSV(deductions);
    const filename = generateFilename('sss', period);
    downloadCSV(csvContent, filename);
  },

  /**
   * Export PhilHealth deductions to CSV
   */
  exportPhilHealth: (deductions: GovernmentDeduction[], period?: string) => {
    const csvContent = convertPhilHealthToCSV(deductions);
    const filename = generateFilename('philhealth', period);
    downloadCSV(csvContent, filename);
  },

  /**
   * Export Pag-IBIG deductions to CSV
   */
  exportPagIBIG: (deductions: GovernmentDeduction[], period?: string) => {
    const csvContent = convertPagIBIGToCSV(deductions);
    const filename = generateFilename('pagibig', period);
    downloadCSV(csvContent, filename);
  },

  /**
   * Export Tax deductions to CSV
   */
  exportTax: (deductions: GovernmentDeduction[], period?: string) => {
    const csvContent = convertTaxToCSV(deductions);
    const filename = generateFilename('tax', period);
    downloadCSV(csvContent, filename);
  },

  /**
   * Export all government deductions to CSV
   */
  exportAllDeductions: (deductions: GovernmentDeduction[], period?: string) => {
    const csvContent = convertAllDeductionsToCSV(deductions);
    const filename = generateFilename('all_government', period);
    downloadCSV(csvContent, filename);
  },

  /**
   * Export summary report to CSV
   */
  exportSummary: (summary: GovernmentDeductionSummary) => {
    const csvContent = convertSummaryToCSV(summary);
    const filename = generateFilename('summary', summary.period);
    downloadCSV(csvContent, filename);
  }
};